import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import React, { Fragment } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import cx from 'classnames'

import BinIcon from 'Icons/delete-icon.svg'
import ActionSelect from '../templates/action-select/ActionSelect'
import { useSelector } from 'react-redux'
import { getAccountDetails } from 'Selectors/account'

const FormLabel = (props) => {
  return <Label className='form-label' {...props} />
}

const EditableInputList = (props) => {
  const { controlName, fields, setValue, update, remove, errors } = props
  const { register, trigger } = useFormContext()

  return (
    <Fragment>
      {fields.map((item, index) => (
        <div
          key={index}
          className={cx('removable-input-wrapper', {
            'is-invalid': errors[`${controlName}.${index}`]
          })}
        >
          <input
            type='text'
            placeholder='Enter message'
            className={cx('form-control', 'input-text', {
              'is-invalid': errors[`${controlName}.${index}`],
              'deletable-input': fields.length > 1
            })}
            name={`${controlName}.${index}.message`}
            {...register(`${controlName}.${index}.message`)}
            onBlur={(e) => {
              const value = e.target.value?.trim() ?? ''
              setValue(`${controlName}.${index}.message`, value)
              trigger(`${controlName}`)
            }}
            onChange={(e) => update(index, { ...item, message: e.target.value })}
            defaultValue={item.message}
          />
          {fields.length > 1 && <BinIcon className='delete-icon' onClick={() => remove(index)} />}
          {errors && errors[`${controlName}.${index}`] && (
            <small className='form-text text-danger'>{errors[`${controlName}.${index}`].message}</small>
          )}
        </div>
      ))}
    </Fragment>
  )
}

const MessagesConfigTab = ({ isNewUI, dynamicFieldErrors }) => {
  const {
    register,
    setValue,
    trigger,
    control,
    formState: { errors }
  } = useFormContext()

  const acknowledgementMessages = useFieldArray({
    control,
    name: 'acknowledgementMessages'
  })
  const feedbackMessages = useFieldArray({
    control,
    name: 'feedbackMessages'
  })

  const accountData = useSelector(getAccountDetails)
  const showEmptyResponseMessage = accountData.settings?.ai_predictions

  return (
    <div className='ml-search-container'>
      <div className='ml-search-ack-section'>
        <FormControl className='ml-form-control' style={{ marginBottom: '1.2rem' }}>
          <FormLabel htmlFor='acknowledgementMessages'>Message shown before showing search results</FormLabel>
          {acknowledgementMessages && acknowledgementMessages.fields && (
            <EditableInputList
              setValue={setValue}
              fields={acknowledgementMessages.fields}
              errors={dynamicFieldErrors}
              controlName='acknowledgementMessages'
              update={(index, item) => acknowledgementMessages.update(index, item)}
              remove={(index) => acknowledgementMessages.remove(index)}
            />
          )}
        </FormControl>
        <FormControl className='ml-form-control'>
          <FormLabel htmlFor='feedbackMessages'>Follow up after displaying the selected answer</FormLabel>
          {feedbackMessages && feedbackMessages.fields && (
            <EditableInputList
              setValue={setValue}
              fields={feedbackMessages.fields}
              errors={dynamicFieldErrors}
              controlName='feedbackMessages'
              update={(index, item) => feedbackMessages.update(index, item)}
              remove={(index) => feedbackMessages.remove(index)}
            />
          )}
        </FormControl>
        {showEmptyResponseMessage && (
          <FormControl className='ml-form-control'>
            <FormLabel htmlFor='emptyResponseMessage'>
              Message shown when bot is unable to answer user question
            </FormLabel>
            <div
              className={cx('removable-input-wrapper', {
                'is-invalid': errors.emptyResponseMessage
              })}
            >
              <textarea
                {...register('emptyResponseMessage')}
                placeholder='Enter message'
                className={cx('form-control', 'input-text', {
                  'is-invalid': errors.emptyResponseMessage
                })}
              />
              {errors.emptyResponseMessage && (
                <small className='form-text text-danger'>{errors.emptyResponseMessage.message}</small>
              )}
            </div>
          </FormControl>
        )}
      </div>

      <div className='ml-search-feedback-section' style={{ paddingTop: '1rem' }}>
        {/* On Success */}
        <div className='form-group feedback-section'>
          <label className='feedback-label success'> On Success </label>
          <div className='input-group'>
            <FormControl className='ml-form-control'>
              <div className='ml-input-label'> Label </div>
              <Fragment>
                <input
                  type='text'
                  className={cx('form-control', 'input-text', { 'is-invalid': errors.helpfulLabel })}
                  name='helpfulLabel'
                  {...register('helpfulLabel')}
                  onBlur={(e) => {
                    const value = e.target.value?.trim() ?? ''
                    setValue('helpfulLabel', value)
                    trigger('helpfulLabel')
                  }}
                  placeholder='Eg. “No, I’m all set!”'
                />
                {errors.helpfulLabel && <small className='form-text text-danger'>{errors.helpfulLabel.message}</small>}
              </Fragment>
            </FormControl>
            <FormControl className='ml-form-control'>
              <Fragment>
                <ActionSelect
                  name='helpfulAction'
                  className={cx({ 'new-design': isNewUI })}
                  labelOptions={{ name: 'Action' }}
                  allowedActions={['goToStep', 'endChat']}
                />
              </Fragment>
            </FormControl>
          </div>
        </div>
        {/* Escalate to Next Step */}
        <div className='form-group feedback-section'>
          <label className='feedback-label next-step'> Escalate to Next Step </label>
          <div className='input-group'>
            <FormControl className='ml-form-control'>
              <div className='ml-input-label'> Label </div>
              <Fragment>
                <input
                  type='text'
                  className={cx('form-control', 'input-text', { 'is-invalid': errors.notHelpfulLabel })}
                  name='notHelpfulLabel'
                  {...register('notHelpfulLabel')}
                  onBlur={(e) => {
                    const value = e.target.value?.trim() ?? ''
                    setValue('notHelpfulLabel', value)
                    trigger('notHelpfulLabel')
                  }}
                  placeholder='Eg. “Contact Support"'
                />
                {errors.notHelpfulLabel && (
                  <small className='form-text text-danger'>{errors.notHelpfulLabel.message}</small>
                )}
              </Fragment>
            </FormControl>
            <FormControl className='ml-form-control'>
              <Fragment>
                <ActionSelect
                  name='notHelpfulAction'
                  className={cx({ 'new-design': isNewUI })}
                  labelOptions={{ name: 'Action' }}
                  allowedActions={['goToStep', 'endChat']}
                />
              </Fragment>
            </FormControl>
          </div>
        </div>
        {/* Need More Information */}
        <div className='form-group feedback-section'>
          <label className='feedback-label need-more-info'> Need More Information </label>
          <div className='input-group'>
            <FormControl className='ml-form-control'>
              <div className='ml-input-label'> Label </div>
              <Fragment>
                <input
                  type='text'
                  className={cx('form-control', 'input-text', { 'is-invalid': errors.noResultLabel })}
                  name='noResultLabel'
                  {...register('noResultLabel')}
                  onBlur={(e) => {
                    const value = e.target.value?.trim() ?? ''
                    setValue('noResultLabel', value)
                    trigger('noResultLabel')
                  }}
                  placeholder='Eg. “Need More Help!”'
                />
                {errors.noResultLabel && (
                  <small className='form-text text-danger'>{errors.noResultLabel.message}</small>
                )}
              </Fragment>
            </FormControl>
            <FormControl className='ml-form-control'>
              <Fragment>
                <ActionSelect
                  name='noResultAction'
                  className={cx({ 'new-design': isNewUI })}
                  labelOptions={{ name: 'Action' }}
                  allowedActions={['goToStep', 'endChat']}
                />
              </Fragment>
            </FormControl>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessagesConfigTab
